import { useEffect, useState } from 'react';
import { Modale } from '../ui/Modale';
import { Icone } from '../Icone';
import { urlCompteRenduEntretien, type LigneEntretien } from '../../lib/useEntretiens';
import type { CorpsErreurApi } from '../../lib/types';

type EtatApercu = { statut: 'chargement' } | { statut: 'ok' } | { statut: 'erreur'; message: string };

/**
 * Compose le nom du fichier PDF du compte-rendu, identique à la convention
 * appliquée côté serveur.
 */
export function nomFichierCompteRendu(entretien: LigneEntretien): string {
  const nom = entretien.interlocuteur
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  const dateIso = entretien.dateEntretien.split('T')[0] ?? '';
  return `compte-rendu-entretien-${nom || 'entretien'}${dateIso ? `-${dateIso}` : ''}.pdf`;
}

/**
 * Consultation du compte-rendu d'entretien en PDF, sans quitter l'écran.
 *
 * Reprend fidèlement la structure et les états de la visionneuse de pièces justificatives
 * du dossier apprenant (vérification en amont par fetch, affichage iframe en sandbox,
 * gestion explicite de l'indisponibilité du document et bouton de téléchargement).
 */
export function VisionneuseCompteRendu({
  ouverte,
  entretien,
  onFermer,
}: {
  ouverte: boolean;
  entretien: LigneEntretien;
  onFermer: () => void;
}): JSX.Element {
  const nomFichier = nomFichierCompteRendu(entretien);
  const base = urlCompteRenduEntretien(entretien.id);
  const affichable = `${base}?inline=1`;
  const telechargeable = `${base}?telecharger=1`;

  const [etat, setEtat] = useState<EtatApercu>({ statut: 'chargement' });

  // Empêche les déclenchements automatiques intempestifs de la boîte d'impression
  // (ex. PDF embarqué avec action JS automatique ou lecteur du navigateur).
  useEffect(() => {
    if (!ouverte) return;
    const printOriginal = window.print;
    window.print = () => {
      // Ignorer l'appel d'impression automatique non sollicité pendant la consultation
    };
    return () => {
      window.print = printOriginal;
    };
  }, [ouverte]);

  // Sonde la route avant de monter l'iframe pour vérifier si le compte-rendu est disponible
  // et intercepter une erreur (ex. fiche non renseignée ou indisponibilité) plutôt qu'une page
  // muette ou cassée dans le lecteur du navigateur.
  useEffect(() => {
    if (!ouverte) return;
    let annule = false;
    const controleur = new AbortController();
    setEtat({ statut: 'chargement' });

    fetch(affichable, { credentials: 'include', signal: controleur.signal })
      .then(async (reponse) => {
        if (reponse.ok) {
          controleur.abort();
          if (!annule) setEtat({ statut: 'ok' });
          return;
        }
        let message = 'Aperçu indisponible.';
        try {
          const corps = (await reponse.json()) as CorpsErreurApi;
          if (corps.erreur?.message) message = corps.erreur.message;
        } catch {
          // Corps non JSON : le message par défaut suffit.
        }
        if (!annule) setEtat({ statut: 'erreur', message });
      })
      .catch((erreur) => {
        if (annule || (erreur instanceof DOMException && erreur.name === 'AbortError')) return;
        setEtat({ statut: 'erreur', message: 'Aperçu indisponible : la connexion a échoué.' });
      });

    return () => {
      annule = true;
      controleur.abort();
    };
  }, [affichable, ouverte]);

  return (
    <Modale
      titre="Compte-rendu d’entretien"
      sousTitre={nomFichier}
      ouverte={ouverte}
      onFermer={onFermer}
      largeur="max-w-5xl"
      zIndice="z-[60]"
    >
      <div className="space-y-3 px-4 py-4 sm:px-6">
        {/* Hauteur en unités de fenêtre : le document occupe la place
            disponible sur tablette comme sur grand écran, sans jamais pousser
            les actions hors de vue. */}
        <div className="relative flex min-h-[50vh] items-center justify-center overflow-auto rounded-xl bg-codialis-sand p-2 sm:min-h-[60vh]">
          {etat.statut === 'chargement' && (
            <p className="text-xs text-codialis-gray">Vérification du fichier…</p>
          )}

          {etat.statut === 'erreur' && (
            <div className="max-w-sm p-4 sm:p-6 text-center">
              <Icone nom="document" className="mx-auto h-8 w-8 text-codialis-gray" />
              <p className="mt-2 text-sm font-medium text-codialis-navy">Aperçu indisponible</p>
              <p className="mt-1 text-xs text-codialis-gray">{etat.message}</p>
            </div>
          )}

          {etat.statut === 'ok' && (
            // Pas d'attribut `sandbox` : Chrome refuse d'ouvrir son lecteur PDF
            // dans une iframe bac à sable, quelles que soient les permissions
            // accordées — « Cette page a été bloquée par Chrome ». Le
            // confinement du document reste assuré côté serveur, par l'en-tête
            // `Content-Security-Policy: sandbox` de la route de téléchargement
            // (origine opaque, ni script ni cookie de session) et par
            // `X-Content-Type-Options: nosniff`.
            <iframe
              key={entretien.id}
              src={affichable}
              title={`Aperçu de ${nomFichier}`}
              className="h-[70vh] w-full rounded-lg border-0 bg-white"
            />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2" />

          {/* Si le compte-rendu est absent ou indisponible, le bouton est désactivé
              pour ne pas provoquer d'erreur ou d'effet indésirable du navigateur. */}
          {etat.statut === 'ok' ? (
            <a
              href={telechargeable}
              download={nomFichier}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <Icone nom="telecharger" className="h-4 w-4" />
              Télécharger
            </a>
          ) : (
            <button
              type="button"
              disabled
              title={etat.statut === 'erreur' ? etat.message : 'Chargement du fichier…'}
              className="inline-flex min-h-[44px] cursor-not-allowed items-center gap-2 rounded-xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-sm font-semibold text-codialis-gray opacity-60"
            >
              <Icone nom="telecharger" className="h-4 w-4" />
              {etat.statut === 'erreur' ? 'Téléchargement indisponible' : 'Vérification…'}
            </button>
          )}
        </div>
      </div>
    </Modale>
  );
}
